import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  FaHubspot,
  FaHistory,
  FaArrowRight,
  FaArrowLeft,
  FaBolt,
  FaBuilding,
  FaUserCheck,
  FaChartLine,
  FaFire,
} from "react-icons/fa";
import { Avatar } from "../OrgUsageAnalytics/components/Avatar";
import { EnvToggle } from "../OrgUsageAnalytics/components/EnvToggle";
import { DateRangeFilter } from "../OrgUsageAnalytics/components/DateRangeFilter";
import { useEnv } from "../OrgUsageAnalytics/useEnv";
import { fetchUsageOverview, dateRangeParams } from "../OrgUsageAnalytics/api";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "./OrgOverview.css";
import OrgPicker, { pushRecentOrg, readRecents } from "./OrgPicker";
import UsageSection from "./sections/UsageSection";
import BillingSection from "./sections/BillingSection";
import LimitsSection from "./sections/LimitsSection";
import AiCostSection from "./sections/AiCostSection";
import ExtendAiSection from "./sections/ExtendAiSection";
import LoanStageSection from "./sections/LoanStageSection";
import ProcessingSection from "./sections/ProcessingSection";
import PcRetentionSection from "./sections/PcRetentionSection";
import VoiceSettingsSection from "./sections/VoiceSettingsSection";
import AdoptionOverviewTabs from "./sections/AdoptionOverviewTabs";
import IntegrationsSection from "./sections/IntegrationsSection";
import { fmtN, fmtMoney, formatRelative } from "./format";

const LIMIT_BADGE = {
  critical: { cls: "oo-chip--danger", label: "At cap" },
  watch: { cls: "oo-chip--warn", label: "Watch" },
  ok: { cls: "oo-chip--ok", label: "Healthy" },
};

const LEADERBOARD_DISPLAY = 10;

const SORTS = [
  { id: "most_active", label: "Most active", icon: <FaFire /> },
  { id: "most_members", label: "Largest teams", icon: <FaBuilding /> },
  { id: "least_active", label: "Dormant", icon: <FaHistory /> },
];

const SORT_HINTS = {
  most_active:
    "Ranked by user-attributed LoanHistory events in the selected period",
  most_members: "Ranked by team size among orgs with activity in the period",
  least_active: "Lowest user-attributed event volume in the selected period",
};

const clampPct = (v) => Math.min(100, Math.max(0, Number(v) || 0));
const adoptColor = (rate) =>
  rate >= 50 ? "#059669" : rate >= 20 ? "#d97706" : "#94a3b8";

/** Compact recents tile (no metrics — recents only carry id + name). */
function RecentTile({ org, onSelect }) {
  return (
    <button
      type="button"
      className="oo-tile"
      onClick={() => onSelect(org)}
      title={org.id}
    >
      <Avatar name={org.name} seed={org.id} size="md" />
      <span className="oo-tile-text">
        <span className="oo-tile-name">{org.name}</span>
        <span className="oo-tile-id">{org.id}</span>
      </span>
      <FaArrowRight className="oo-tile-arrow" />
    </button>
  );
}

/** Rich, metric-bearing org card used in the leaderboard. */
function LeaderCard({ org, rank, onSelect }) {
  const hasMembers = (org.totalMembers || 0) > 0;
  const rate = hasMembers ? clampPct(org.adoptionRate) : null;

  let activityLabel = "No user activity";
  if (org.topFeature) {
    activityLabel = null; // rendered with chip below
  } else if ((org.totalEvents || 0) > 0) {
    activityLabel = `${fmtN(org.totalEvents)} events`;
  } else if ((org.loansTouched || 0) > 0) {
    activityLabel = `${fmtN(org.loansTouched)} loans touched (system)`;
  }

  return (
    <button
      type="button"
      className="oo-orgcard"
      onClick={() =>
        onSelect({ id: org.organizationId, name: org.organizationName })
      }
      title={org.organizationId}
    >
      <div className="oo-orgcard-top">
        <span className="oo-orgcard-rank">#{rank}</span>
        <Avatar
          name={org.organizationName}
          seed={org.organizationId}
          size="md"
        />
        <span className="oo-orgcard-identity">
          <span className="oo-orgcard-name">{org.organizationName}</span>
          <span className="oo-orgcard-id">{org.organizationId}</span>
        </span>
        <span
          className="oo-orgcard-dot"
          style={{ background: adoptColor(rate ?? 0) }}
          title={rate !== null ? `${rate}% adoption` : "Adoption unavailable"}
        />
      </div>

      <div className="oo-orgcard-adopt">
        <div className="oo-orgcard-adopt-top">
          <span>
            {hasMembers ? (
              <>
                <strong>{fmtN(org.uniqueActiveUsers)}</strong> /{" "}
                {fmtN(org.totalMembers)} active
              </>
            ) : (
              <>
                <strong>{fmtN(org.uniqueActiveUsers)}</strong> active users
              </>
            )}
          </span>
          <strong>{rate !== null ? `${rate}%` : "—"}</strong>
        </div>
        <div className="oo-meter-track">
          <div
            className="oo-meter-fill"
            style={{
              width: `${Math.max(3, rate ?? 0)}%`,
              background: adoptColor(rate ?? 0),
            }}
          />
        </div>
      </div>

      <div className="oo-orgcard-stats">
        <div>
          <div className="oo-orgcard-stat-num">{fmtN(org.totalEvents)}</div>
          <div className="oo-orgcard-stat-lbl">Events</div>
        </div>
        <div>
          <div className="oo-orgcard-stat-num">{fmtN(org.loansTouched)}</div>
          <div className="oo-orgcard-stat-lbl">Loans</div>
        </div>
        <div>
          <div className="oo-orgcard-stat-num">{fmtN(org.totalMembers)}</div>
          <div className="oo-orgcard-stat-lbl">Members</div>
        </div>
      </div>

      <div className="oo-orgcard-meta">
        <span className="oo-chip">
          {org.topFeature ? (
            <>
              Top: <strong>{org.topFeature}</strong>
            </>
          ) : (
            activityLabel
          )}
        </span>
        <span className="oo-orgcard-time">
          {formatRelative(org.lastActivityAt)}
        </span>
      </div>
    </button>
  );
}

function PulseStat({ icon, label, value }) {
  return (
    <div className="oo-pulse">
      <span className="oo-pulse-icon">{icon}</span>
      <span className="oo-pulse-body">
        <span className="oo-pulse-value">{value}</span>
        <span className="oo-pulse-label">{label}</span>
      </span>
    </div>
  );
}

/**
 * Org 360 command center — the pre-selection landing. Rather than an empty page
 * it shows a live pulse of the whole tenant base, one-click access to recently
 * viewed orgs, and a sortable leaderboard of organizations with real signal
 * (adoption, activity, team size) sourced from the same usage-overview endpoint
 * the Users Usage index uses. Picking any card drops you into that org's 360.
 */
function LandingView({ env, days, onSelect }) {
  const recents = useMemo(() => readRecents(), []);
  const [sort, setSort] = useState("most_active");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchUsageOverview(
      {
        ...dateRangeParams(days),
        sort,
        activeOnly: "false",
        limit: String(LEADERBOARD_DISPLAY),
        offset: "0",
      },
      env,
    )
      .then((json) => {
        if (cancelled) return;
        setData(json);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Failed to load organization activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, days, sort]);

  const totals = data?.globalTotals;
  const orgs = data?.orgs || [];

  return (
    <div className="oo-landing">
      <div className="oo-landing-hero">
        <div className="oo-landing-hero-icon">
          <FaHubspot />
        </div>
        <div className="oo-landing-hero-copy">
          <div className="oo-eyebrow">Organization 360 · Command center</div>
          <h2 className="oo-landing-hero-title">
            Pick an organization, see everything
          </h2>
          <p className="oo-landing-hero-sub">
            Usage, billing, AI spend, limits, and history in one view. Search up
            top, jump back into a recent org, or dive into the most active
            organizations below.
          </p>
        </div>
        <div className="oo-pulse-strip">
          <PulseStat
            icon={<FaBuilding />}
            label="Organizations"
            value={totals ? fmtN(totals.totalOrgs) : "—"}
          />
          <PulseStat
            icon={<FaBolt />}
            label="Active orgs"
            value={totals ? fmtN(totals.orgsWithActivity) : "—"}
          />
          <PulseStat
            icon={<FaUserCheck />}
            label="Active users"
            value={totals ? fmtN(totals.totalUniqueUsersActive) : "—"}
          />
          <PulseStat
            icon={<FaChartLine />}
            label={`Events · ${days}d`}
            value={totals ? fmtN(totals.totalEvents) : "—"}
          />
        </div>
      </div>

      {recents.length ? (
        <div className="oo-landing-block">
          <div className="oo-landing-block-head">
            <FaHistory /> Jump back in
          </div>
          <div className="oo-tile-grid">
            {recents.map((org) => (
              <RecentTile key={`r-${org.id}`} org={org} onSelect={onSelect} />
            ))}
          </div>
        </div>
      ) : null}

      <div className="oo-landing-block">
        <div className="oo-landing-block-head">
          <FaFire /> Organizations
          <div className="oo-seg" role="tablist">
            {SORTS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`oo-seg-btn ${sort === s.id ? "oo-seg-btn--on" : ""}`}
                onClick={() => setSort(s.id)}
              >
                {s.icon}
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <p className="oo-landing-sort-hint">{SORT_HINTS[sort]}</p>

        {error ? (
          <div className="oo-empty-hint">
            {error} — use the search box above to open an organization directly.
          </div>
        ) : loading ? (
          <div className="oo-orgcard-grid">
            {Array.from({ length: LEADERBOARD_DISPLAY }).map((_, i) => (
              <div key={i} className="oo-orgcard oo-orgcard--skel">
                <div className="oo-orgcard-top">
                  <span
                    className="ou-skel"
                    style={{ width: 38, height: 38, borderRadius: 10 }}
                  />
                  <span className="ou-skel ou-skel-line" style={{ flex: 1 }} />
                </div>
                <div
                  className="ou-skel"
                  style={{ height: 8, margin: "14px 0" }}
                />
                <div className="ou-skel" style={{ height: 40 }} />
              </div>
            ))}
          </div>
        ) : orgs.length ? (
          <div className="oo-orgcard-grid">
            {orgs.map((org, i) => (
              <LeaderCard
                key={org.organizationId}
                org={org}
                rank={i + 1}
                onSelect={onSelect}
              />
            ))}
          </div>
        ) : (
          <div className="oo-empty-hint">
            No organization activity in this window. Try a wider date range or
            search above.
          </div>
        )}
      </div>

      <AdoptionOverviewTabs env={env} onSelect={onSelect} />
    </div>
  );
}

function HeaderStat({ label, value, ready }) {
  return (
    <div className="oo-stat">
      {ready ? (
        <span className="oo-stat-value">{value}</span>
      ) : (
        <span className="ou-skel oo-stat-skel" />
      )}
      <span className="oo-stat-label">{label}</span>
    </div>
  );
}

export default function OrgOverviewPage({ onReady }) {
  const { organizationId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [env, setEnv] = useEnv();
  const [days, setDays] = useState(7);
  const [metrics, setMetrics] = useState({});

  const [selectedOrg, setSelectedOrg] = useState(() =>
    organizationId
      ? {
          id: organizationId,
          name: location.state?.organizationName || organizationId,
        }
      : null,
  );

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  // Keep local selection in sync with the URL (back/forward, refresh, deep links).
  useEffect(() => {
    if (!organizationId) {
      setSelectedOrg(null);
      return;
    }
    setSelectedOrg((prev) =>
      prev && prev.id === organizationId
        ? prev
        : {
            id: organizationId,
            name: location.state?.organizationName || organizationId,
          },
    );
  }, [organizationId, location.state]);

  // Reset aggregated header metrics whenever the scope changes.
  useEffect(() => {
    setMetrics({});
  }, [organizationId, env, days]);

  const reportMetrics = useCallback((patch) => {
    setMetrics((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleSelect = useCallback(
    (org) => {
      pushRecentOrg(org);
      setSelectedOrg(org);
      history.push(`/org-overview/${org.id}`, { organizationName: org.name });
    },
    [history],
  );

  // Deselect → return to the command center landing.
  const handleClear = useCallback(() => {
    setSelectedOrg(null);
    history.push("/org-overview");
  }, [history]);

  const orgName = selectedOrg?.name || organizationId;
  // Sections share one key so switching org/env gives every section a clean
  // slate; the day-range change refetches in place via each section's effect.
  const scopeKey = `${organizationId}:${env}`;

  const limitBadge =
    metrics.limitSeverity && LIMIT_BADGE[metrics.limitSeverity]
      ? LIMIT_BADGE[metrics.limitSeverity]
      : null;

  const sections = useMemo(() => {
    if (!organizationId) return null;
    return (
      <div className="oo-grid">
        <UsageSection
          key={`usage-${scopeKey}`}
          organizationId={organizationId}
          orgName={orgName}
          env={env}
          days={days}
          onMetrics={reportMetrics}
        />
        <AiCostSection
          key={`ai-${scopeKey}`}
          organizationId={organizationId}
          env={env}
          days={days}
          onMetrics={reportMetrics}
        />
        <BillingSection
          key={`billing-${scopeKey}`}
          organizationId={organizationId}
          orgName={orgName}
          env={env}
          onMetrics={reportMetrics}
        />
        <LimitsSection
          key={`limits-${scopeKey}`}
          organizationId={organizationId}
          env={env}
          onMetrics={reportMetrics}
        />
        <ExtendAiSection
          key={`extend-${scopeKey}`}
          organizationId={organizationId}
          env={env}
          days={days}
        />
        <IntegrationsSection
          key={`integrations-${scopeKey}`}
          organizationId={organizationId}
          env={env}
        />
        <LoanStageSection
          key={`stage-${scopeKey}`}
          organizationId={organizationId}
          env={env}
        />
        <ProcessingSection
          key={`proc-${scopeKey}`}
          organizationId={organizationId}
          env={env}
        />
        <PcRetentionSection
          key={`retention-${scopeKey}`}
          organizationId={organizationId}
          env={env}
        />
        <VoiceSettingsSection
          key={`voice-${scopeKey}`}
          organizationId={organizationId}
          orgName={orgName}
          env={env}
        />
      </div>
    );
  }, [organizationId, orgName, env, days, scopeKey, reportMetrics]);

  return (
    <div className="ou-page oo-page">
      <div className="oo-topbar">
        <div className="oo-topbar-copy">
          <div className="oo-eyebrow">
            <FaHubspot />
            Organization 360
          </div>
          <h1 className="oo-title">Org Overview</h1>
          <p className="oo-subtitle">
            Everything about one organization in a single view — usage, billing,
            limits, AI cost, and history. Pick an org to begin.
          </p>
        </div>
        <div className="oo-topbar-actions">
          <OrgPicker
            env={env}
            selectedOrg={selectedOrg}
            onSelect={handleSelect}
            onClear={organizationId ? handleClear : undefined}
          />
          <EnvToggle env={env} onChange={setEnv} />
          <DateRangeFilter days={days} onChange={setDays} />
        </div>
      </div>

      {!organizationId ? (
        <LandingView env={env} days={days} onSelect={handleSelect} />
      ) : (
        <>
          <header className="oo-header">
            <button
              type="button"
              className="oo-back-btn"
              onClick={handleClear}
              title="Back to all organizations"
            >
              <FaArrowLeft />
            </button>
            <div className="oo-header-id">
              <Avatar name={orgName} seed={organizationId} size="lg" />
              <div className="oo-header-meta">
                <h2 className="oo-header-name">
                  {orgName}
                  {limitBadge ? (
                    <span
                      className={`oo-chip ${limitBadge.cls}`}
                      style={{ marginLeft: 10 }}
                    >
                      {limitBadge.label}
                    </span>
                  ) : null}
                </h2>
                <div className="oo-header-orgid">{organizationId}</div>
              </div>
            </div>
            <div className="oo-header-stats">
              <HeaderStat
                label="Adoption"
                value={`${metrics.adoptionPct ?? 0}%`}
                ready={metrics.adoptionPct !== undefined}
              />
              <HeaderStat
                label="Active users"
                value={fmtN(metrics.activeUsers)}
                ready={metrics.activeUsers !== undefined}
              />
              <HeaderStat
                label="Members"
                value={fmtN(metrics.totalMembers)}
                ready={metrics.totalMembers !== undefined}
              />
              <HeaderStat
                label="Loans accessed"
                value={fmtN(metrics.loansAccessed)}
                ready={metrics.loansAccessed !== undefined}
              />
              <HeaderStat
                label="Billable (mo)"
                value={
                  metrics.billableLoans !== undefined
                    ? fmtN(metrics.billableLoans)
                    : "—"
                }
                ready
              />
              <HeaderStat
                label="AI cost"
                value={
                  metrics.aiCost !== undefined ? fmtMoney(metrics.aiCost) : "—"
                }
                ready
              />
            </div>
          </header>

          {sections}
        </>
      )}
    </div>
  );
}
